package pl.decks.personal.pages.ievp.foreword

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.dom.svg.Group
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowUpRightFromSquare
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet
import pl.decks.personal.components.widgets.TrailingIconRow
import pl.decks.personal.components.widgets.button.ButtonStylesheet
import pl.decks.personal.components.widgets.poissondiskgrid.meshStrokeColor
import pl.decks.personal.components.widgets.poissondiskgrid.meshStrokeWidth
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.ievp.IEVPStylesheet
import pl.decks.personal.styles.SiteLayout

private val NOTE_TEXTS = listOf(
    "Focuses on things which are important, not building a facade",
    "Classical marketing, as understood by market, is of tertiary importance",
    "Actually enjoying the content, not forced, not salesy",
    "Wished I researched them while on Exura",
    "Engineers close to customers. Clients are actual stakeholders",
    "Not enterprise focused",
    "Understand their client-base; one I can relate to",
    "Really feels like a conglomerate of small startups",
    "\"don't join if mainly wanting to lead others [..] want people to lead by example\"",
    "Direct, honest feedback",
    "Get stuff done by yourself",
    "Extreme ownership, extreme transparency",
    "Definition and approach to \"weird\"; I like character and out-of-the-box",
    "Deep work - minimal meetings",
    "Try first culture",
    "BookHog + Philosophy club",
)

@Composable
fun PostHogForeword() = Column(
    modifier = Modifier.fillMaxWidth()
        .setVariable(meshStrokeColor, ColorMode.current.toSitePalette().accents.secondary.toRgb().copyf(alpha = 0.5f))
        .setVariable(meshStrokeWidth, 3)
        .setVariable(ForewordStylesheet.Vars.pointBackgroundColor, ColorMode.current.toSitePalette().surface)
        .setVariable(ForewordStylesheet.Vars.pointVisitedBackgroundColor, ColorMode.current.toSitePalette().accents.secondary)
        .setVariable(ForewordStylesheet.Vars.pointStrokeColor, meshStrokeColor.value()),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    PostHogForewordContent()
    HogTrack(delay = PostHogStylesheet.Vars.traverseCycleDuration.value() * 0.35)
}

@Composable
private fun PostHogForewordContent() = Column(
    modifier = SiteLayoutStylesheet.Page.Content.toModifier()
        .padding(left = IEVPStylesheet.Vars.contentPaddingLeft.value(), top = SiteLayout.Space.Medium)
        .gap(SiteLayout.Space.Small),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    CoverLetterText(
        "See, I've fallen for PostHog deep. The extraordinary culture leaks through the docs - open, transparent, weird. I know you read customization as a signal of excitement. Still, I decided not to swap a word below. First, it would go against the concept's spirit but, honestly, the IEVP felt targeted at you already. Take my word or check it, the code is public. Then I thought: Maybe the custom foreword is enough of a signal?",
        "Or the dashboard built in PostHog to see how readers journey through the page?",
    )
    Link(
        path = "https://eu.posthog.com/shared/tdqqrjdRSh17Oy8HgJAQx2NT7ky7jg",
        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
    ) {
        Button(onClick = {}, variant = ButtonStylesheet.GhostVariant) {
            TrailingIconRow(icon = { FaArrowUpRightFromSquare() }) { SpanText("IEVP Journeys") }
        }
    }
    CoverLetterText(
        "Or reading through the docs at least thrice to note the things I admire? Ending up with so many that I had to select few and present them as taken:",
    )
    NotesConstellation(NOTE_TEXTS)
    CoverLetterText("Hell, don't you see the hedgehog zooming through the screen?!")
}

@Composable
private fun HogTrack(modifier: Modifier = Modifier, delay: CSSNumericValue<out CSSUnitTime>? = null) = Box(
    Modifier
        .fillMaxWidth()
        .overflow(Overflow.Hidden)
        .height(56.px)
        .then(modifier),
    contentAlignment = Alignment.BottomStart,
) {
    Box(
        Modifier
            .height(50.percent)
            .position(Position.Relative)
            .animation(traverseAnimation(PostHogStylesheet.TraverseScreenFrames, delay))
    ) {
        PostHogLogo(
            Modifier
                .fillMaxHeight()
                .animation(traverseAnimation(PostHogStylesheet.JumpFrames, delay))
        )
    }
}

@Composable
private fun PostHogLogo(modifier: Modifier = Modifier) = Svg(
    attrs = Modifier.aspectRatio(48, 28).then(modifier)
        .toAttrs {
            attr("viewBox", "0 0 48 28")
            attr("xmlns", "http://www.w3.org/2000/svg")
        }
) {
    Path(
        Modifier.animation(alterColorsAnimation(3)).toAttrs {
            d(
                "M0 22.4082C0 21.5173 1.07707 21.0712 1.70703 21.7012L6.29004 26.2842C6.92 26.9141 " +
                        "6.47391 27.9912 5.58301 27.9912H1C0.447715 27.9912 0 27.5435 0 26.9912V22.4082Z" +
                        "M0 12.4112C0 11.5203 1.07707 11.0742 1.70703 11.7041L9.99707 19.9942V27.9912L" +
                        "0.292969 18.2871C0.105496 18.0996 0 17.8453 0 17.5801V12.4112Z" +
                        "M0 2.41412C4.22779e-05 1.52327 1.07708 1.07718 1.70703 1.70709L9.99707 9.99711V17.9942L" +
                        "0.292969 8.29008C0.10552 8.10256 0 7.8482 0 7.58306V2.41412Z"
            )
            attr("fill", "currentColor")
        }
    )

    Path(
        Modifier.animation(alterColorsAnimation(2)).toAttrs {
            d(
                "M16.2861 26.2842C16.9161 26.9141 16.47 27.9912 15.5791 27.9912H9.99609V19.9942L" +
                        "16.2861 26.2842Z" +
                        "M19.7002 19.7012C19.8877 19.8887 19.9932 20.143 19.9932 20.4082V27.9912L" +
                        "10.2891 18.2871C10.1016 18.0996 9.99609 17.8453 9.99609 17.5801V9.99711L19.7002 19.7012Z" +
                        "M9.99609 2.41412C9.99613 1.52326 11.0732 1.07718 11.7031 1.70709L19.7002 9.70414C19.8877 " +
                        "9.89167 19.9932 10.146 19.9932 10.4112V17.9942L10.2891 8.29008C10.1016 8.10256 " +
                        "9.99609 7.8482 9.99609 7.58305V2.41412Z"
            )
            attr("fill", "currentColor")
        }
    )
    Path(
        Modifier.animation(alterColorsAnimation(1)).toAttrs {
            d(
                "M16.2871 26.2842C16.9171 26.9141 16.471 27.9912 15.5801 27.9912H15.5791C16.47 27.9912 " +
                        "16.9161 26.9141 16.2861 26.2842L9.99707 19.9951V19.9941L16.2871 26.2842Z" +
                        "M26.2842 26.2842C26.9141 26.9141 26.4681 27.9912 25.5771 27.9912H20.4082C20.2633 " +
                        "27.9912 20.1224 27.9585 19.9932 27.8994V20.4082C19.9931 20.143 19.8877 19.8887 " +
                        "19.7002 19.7012L9.99707 9.99805V9.99707L26.2842 26.2842Z" +
                        "M11.0283 1.41211C11.2662 1.41789 11.507 1.51 11.7041 1.70703L29.6982 19.7012C29.8857 " +
                        "19.8887 29.9912 20.143 29.9912 20.4082V25.5771C29.9912 26.4681 28.9141 26.9141 " +
                        "28.2842 26.2842L19.9932 17.9932V10.4111C19.9931 10.146 19.8877 9.89162 19.7002 " +
                        "9.7041L11.7031 1.70703C11.5063 1.51027 11.2659 1.41812 11.0283 1.41211Z" +
                        "M19.9941 2.41406C19.9943 1.52326 21.0712 1.07711 21.7012 1.70703L29.6982 9.7041C29.8857 " +
                        "9.89161 29.9912 10.146 29.9912 10.4111V15.5801C29.9912 16.471 28.9141 16.9171 " +
                        "28.2842 16.2871L20.2871 8.29004C20.0996 8.10251 19.9941 7.84817 19.9941 7.58301V2.41406Z",
            )
            attr("fill", "currentColor")
        }
    )
    Group {
        Path(
            Modifier.animation(alterColorsAnimation(0)).toAttrs {
                d(
                    "M42.5261 22.5308L33.1134 13.1181C32.4834 12.4881 31.4062 12.9343 31.4062 " +
                            "13.8252V26.9916C31.4062 27.5439 31.854 27.9916 32.4062 27.9916H46.9869C47.5392 " +
                            "27.9916 47.9869 27.5439 47.9869 26.9916V25.7926C47.9869 25.2403 47.5373 24.7992 " +
                            "46.9896 24.7279C45.3088 24.5091 43.7368 23.7415 42.5261 22.5308Z" +
                            "M36.2048 24.7926C35.3219 24.7926 34.6053 24.076 34.6053 23.193C34.6053 22.3101 " +
                            "35.3219 21.5935 36.2048 21.5935C37.0877 21.5935 37.8043 22.3101 37.8043 23.193C" +
                            "37.8043 24.076 37.0877 24.7926 36.2048 24.7926Z"
                )
                attr("fill", "currentColor")
            }
        )
    }
}

@Composable
private fun alterColorsAnimation(ind: Int) = PostHogStylesheet.ColorAlterFrames.toAnimation(
    duration = PostHogStylesheet.Vars.colorCycleDuration.value() * 4,
    timingFunction = AnimationTimingFunction.EaseInOut,
    delay = PostHogStylesheet.Vars.colorCycleDuration.value() * (-ind),
    iterationCount = AnimationIterationCount.Infinite,
    direction = AnimationDirection.Normal,
    fillMode = AnimationFillMode.Both,
)

@Composable
private fun traverseAnimation(keyframes: Keyframes, delay: CSSNumericValue<out CSSUnitTime>? = null) =
    keyframes.toAnimation(
        duration = PostHogStylesheet.Vars.traverseCycleDuration.value(),
        timingFunction = AnimationTimingFunction.Linear,
        iterationCount = AnimationIterationCount.Infinite,
        direction = AnimationDirection.Normal,
        delay = delay,
    )

object PostHogStylesheet {
    object Vars {
        val colorCycleDuration by StyleVariable(TransitionDurationVars.UltraSlow.value() * 1)
        val traverseCycleDuration by StyleVariable(TransitionDurationVars.UltraSlow.value() * 10)
    }

    object PostHogColors {
        val key = Color.rgb(0, 0, 0)
        val blue = Color.rgb(29, 74, 255)
        val red = Color.rgb(245, 78, 0)
        val yellow = Color.rgb(249, 189, 43)

        val order = listOf(key, yellow, red, blue)
    }

    val ColorAlterFrames = Keyframes {
        from { Modifier.color(colorMode.toPalette().color) }
        12.5.percent { Modifier.color(colorMode.toPalette().color) }
        25.percent { Modifier.color(PostHogColors.order[1]) }
        37.5.percent { Modifier.color(PostHogColors.order[1]) }
        50.percent { Modifier.color(PostHogColors.order[2]) }
        62.5.percent { Modifier.color(PostHogColors.order[2]) }
        62.5.percent { Modifier.color(PostHogColors.order[2]) }
        75.percent { Modifier.color(PostHogColors.order[3]) }
        87.5.percent { Modifier.color(PostHogColors.order[3]) }
        to { Modifier.color(colorMode.toPalette().color) }
    }

    val TraverseScreenFrames = Keyframes {
        from {
            Modifier
                .translateX(-1 * SiteLayout.Fraction.All)
                .left(SiteLayout.Fraction.none)
                .scaleX(1)
        }
        28.percent {
            Modifier
                .translateX(-1 * SiteLayout.Fraction.Half)
                .left(SiteLayout.Fraction.Half)
                .scaleX(1)
        }
        42.percent {
            Modifier
                .translateX(-1 * SiteLayout.Fraction.Half)
                .left(SiteLayout.Fraction.Half)
                .scaleX(1)
        }
        70.percent {
            Modifier
                .translateX(SiteLayout.Fraction.none)
                .left(SiteLayout.Fraction.All)
                .scaleX(1)
        }
        72.percent {
            Modifier
                .translateX(SiteLayout.Fraction.none)
                .left(SiteLayout.Fraction.All)
                .scaleX(-1)
        }
        98.percent {
            Modifier
                .translateX(-1 * SiteLayout.Fraction.All)
                .left(SiteLayout.Fraction.none)
                .scaleX(-1)
        }
        to {
            Modifier
                .translateX(-1 * SiteLayout.Fraction.All)
                .left(SiteLayout.Fraction.none)
                .scaleX(1)
        }
    }
    val JumpFrames = Keyframes {
        from { Modifier.translateY(SiteLayout.Fraction.none) }
        3.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        7.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        10.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        14.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        17.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        21.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        24.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        28.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        42.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        45.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        49.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        52.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        56.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        59.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        63.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        66.5.percent { Modifier.translateY(-1 * SiteLayout.Fraction.Half) }
        70.percent { Modifier.translateY(SiteLayout.Fraction.none) }
        to { Modifier.translateY(SiteLayout.Fraction.none) }
    }
}
